import React, { createContext, FC, useCallback, useEffect, useMemo } from 'react';
import { IServerSidePaginatedFetcherParams } from './Fetcher';
import { useHistory } from 'react-router-dom';
import { IQuickFilter } from '../components/EasyFilter/QuickFilters';
import { useEnumState } from '../hooks/useEnumState';

interface IFilteringContext {
  preventFetch: boolean;
  activateQuickFilter: (filter: IQuickFilter, historyMethod?: 'push' | 'replace') => void;
  deactivateQuickFilter: (filter: IQuickFilter, historyMethod?: 'push' | 'replace') => void;
}

export const FilteringContext = createContext<IFilteringContext>({
  preventFetch: false,
  activateQuickFilter: () => null,
  deactivateQuickFilter: () => null,
});

interface IProps {
  defaultSorting?: IServerSidePaginatedFetcherParams['sort'];
  defaultFiltering?: IServerSidePaginatedFetcherParams['filter'];
  quickFilters?: Map<string, IQuickFilter>;
}

export const FilteringContextProvider: FC<IProps> = ({ children, defaultFiltering, defaultSorting, quickFilters }) => {
  const history = useHistory();

  /**
   * if there is default sorting or filtering, let's postpone the initial render until the url is settled
   */
  const [quickFiltersReady, setQuickFiltersReady] = useEnumState(
    ![...(quickFilters?.values() ?? [])].some((filter) => filter.isDefault),
    true
  );
  const [filteringReady, setFilteringReady] = useEnumState(!defaultFiltering, true);
  const [sortingReady, setSortingReady] = useEnumState(!defaultSorting, true);

  const onActivate = useCallback<IFilteringContext['activateQuickFilter']>((quickFilter, historyMethod = 'push') => {
    const searchParams = new URLSearchParams(history.location.search);

    if (
      typeof quickFilter.filterOp === 'string' &&
      typeof quickFilter.filterField === 'string' &&
      typeof quickFilter.filterVal === 'string'
    ) {
      searchParams.set('filterOp', quickFilter.filterOp);
      searchParams.set('filterVal', quickFilter.filterVal);
      searchParams.set('filterField', quickFilter.filterField);
    }

    if (quickFilter.extra) {
      Object.entries(quickFilter.extra).forEach(([key, value]) => {
        if (value) {
          searchParams.set(key, value);
        }
      });
    }

    searchParams.delete('page');
    searchParams.delete('pageSize');

    if (quickFilter.sortBy && quickFilter.sortOrder) {
      searchParams.set('sortBy', quickFilter.sortBy);
      searchParams.set('sortOrder', quickFilter.sortOrder);
    }

    searchParams.delete('text');

    history[historyMethod](`?${searchParams.toString()}`);
    setQuickFiltersReady();
  }, []);

  const onDeactivate = useCallback<IFilteringContext['deactivateQuickFilter']>((filter, historyMethod = 'push') => {
    const searchParams = new URLSearchParams(history.location.search);
    searchParams.delete('filterOp');
    searchParams.delete('filterVal');
    searchParams.delete('filterField');
    if (filter.sortBy && filter.sortOrder) {
      searchParams.delete('sortBy');
      searchParams.delete('sortOrder');
    }
    searchParams.delete('page');
    searchParams.delete('pageSize');

    if (filter.extra) {
      Object.keys(filter.extra).forEach((key) => {
        searchParams.delete(key);
      });
    }

    history[historyMethod](`?${searchParams.toString()}`);
  }, []);

  useEffect(() => {
    const defaultQuickFilter = [...(quickFilters?.values() ?? [])].find((filter) => filter.isDefault);

    const search = new URLSearchParams(history.location.search);

    if (defaultQuickFilter) {
      if (!search.has('filterField')) {
        onActivate(defaultQuickFilter, 'replace');
      } else {
        setQuickFiltersReady();
      }
    }
  }, []);

  useEffect(() => {
    if (defaultFiltering) {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('filterOp', defaultFiltering.filterOp);
      searchParams.set('filterVal', defaultFiltering.filterValue);
      searchParams.set('filterField', defaultFiltering.filterField);

      history.replace(`?${searchParams.toString()}`);
      setFilteringReady();
    }
  }, []);

  useEffect(() => {
    if (defaultSorting) {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('sortBy', defaultSorting.sortBy);
      searchParams.set('sortOrder', defaultSorting.sortOrder);

      history.replace(`?${searchParams.toString()}`);
      setFilteringReady();
      setSortingReady();
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      preventFetch: !quickFiltersReady || !filteringReady || !sortingReady,
      activateQuickFilter: onActivate,
      deactivateQuickFilter: onDeactivate,
    }),
    [onActivate, onDeactivate, quickFiltersReady, filteringReady, sortingReady]
  );

  return <FilteringContext.Provider value={contextValue}>{children}</FilteringContext.Provider>;
};
